/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, AlertTriangle, Clock, Info } from 'lucide-react';
import TecnicoLayout from '../components/TecnicoLayout';
import { notificheAPI } from '../api/notifiche';
import { fmtDataOra } from '../utils/date';

const ACCENT = '#10B981';

// Gli avvisi sulle sessioni arrivano quando l'amministratore chiude, corregge o
// annulla un timer: sono i piu' importanti per chi lavora, perche' cambiano le ore
// registrate. Per questo hanno un'icona propria e non quella generica.
function aspetto(tipo) {
  if (tipo === 'sessione') return { Icona: Clock, colore: '#B45309', sfondo: '#FEF3C7' };
  if (tipo === 'warning') return { Icona: AlertTriangle, colore: '#DC2626', sfondo: '#FEE2E2' };
  return { Icona: Info, colore: ACCENT, sfondo: '#D1FAE5' };
}

export default function Notifiche() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: notifiche = [], isLoading } = useQuery({
    queryKey: ['notifiche'],
    queryFn: () => notificheAPI.lista(),
  });

  const segnaLetta = useMutation({
    mutationFn: (id) => notificheAPI.segnaLetta(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifiche'] }),
  });
  const leggiTutte = useMutation({
    mutationFn: () => notificheAPI.leggiTutte(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifiche'] }),
  });

  const nonLette = notifiche.filter((n) => !n.letta).length;

  const apri = (n) => {
    if (!n.letta) segnaLetta.mutate(n.id);
    if (n.link) navigate(n.link);
    else if (n.intervento_id) navigate(`/interventi/${n.intervento_id}`);
  };

  return (
    <TecnicoLayout title="Avvisi">
      {nonLette > 0 && (
        <button onClick={() => leggiTutte.mutate()} style={styles.leggiTutteBtn}>
          <CheckCheck size={16} /> Segna tutti come letti
        </button>
      )}

      {isLoading ? (
        <div style={styles.loading}>Caricamento...</div>
      ) : notifiche.length === 0 ? (
        <div style={styles.empty}>
          <Bell size={40} color="#9CA3AF" />
          <p>Nessun avviso.</p>
        </div>
      ) : (
        <div style={styles.list}>
          {notifiche.map((n) => {
            const { Icona, colore, sfondo } = aspetto(n.tipo);
            const apribile = Boolean(n.link || n.intervento_id);
            return (
              <div
                key={n.id}
                onClick={() => apri(n)}
                style={{
                  ...styles.card,
                  backgroundColor: n.letta ? 'white' : '#ECFDF5',
                  cursor: apribile ? 'pointer' : 'default',
                }}
              >
                <div style={{ ...styles.iconBox, backgroundColor: sfondo }}>
                  <Icona size={18} color={colore} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.cardHead}>
                    <span style={styles.cardTitle}>{n.titolo}</span>
                    {!n.letta && <span style={styles.newDot} />}
                  </div>
                  <div style={styles.cardMsg}>{n.messaggio}</div>
                  <div style={styles.cardTime}>{fmtDataOra(n.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </TecnicoLayout>
  );
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  leggiTutteBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', marginBottom: '1.25rem', color: ACCENT },
  empty: { padding: '3rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', backgroundColor: 'white', borderRadius: '12px' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  card: { display: 'flex', gap: '0.85rem', padding: '1rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  iconBox: { width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardHead: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  cardTitle: { fontWeight: 700, fontSize: '0.92rem' },
  newDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: ACCENT, flexShrink: 0 },
  cardMsg: { fontSize: '0.85rem', color: '#4B5563', marginTop: '0.25rem', lineHeight: 1.45 },
  cardTime: { fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.4rem' },
};
